import { periodKind, restApiKey, table, viewId } from '../../../../_constants/Code'
import { errorLog } from '../../../../common/logger/Logger'
import { getDataCreateTask, getDynamoDB } from '../../../../common/utils/DynamoDbUtils'
import { ClientError } from './../../../../common/utils/Exception'
import {
  convertFiscalPeriodToYear,
  convertFiscalYearToPeriod,
  createExtraYearList,
  extractYearFromFiscalYear,
  getLastDateOfFiscalPeriod
} from './helpers'
import { httpGet, httpPost } from '../../../../services/api/AmplifyApi'
import { PathInfoList } from '../../../../_constants/PathInfoList'
import { assertSecuritiesReportPostRequest, PageState } from './types'

/**
 * @function _getSessionData Redux状態から最新情報を取得
 * @param {any} store
 * @returns {{
 *   corporationNumber: string
 *   userId: string
 *   userName: string
 *   corporationName: string
 * }}
 */
const _getSessionData = (store) => {
  let { mainStore: state, ...rest } = store.getState()

  if (!state) state = rest

  return {
    corporationNumber: state?.company?.corporationNumber,
    userId: state?.user?.sub,
    userName: state?.user?.userName,
    corporationName: state?.company?.name,
    accPeriod: state?.company?.accPeriod,
    state: rest
  }
}

/**
 * マスター情報の取得
 *
 * @function fetchSecuritiesMaster
 * @param {import('./store').SecuritiesReportState} store
 */
export async function fetchSecuritiesMaster(store) {
  const { corporationNumber, userId } = _getSessionData(store)
  try {
    const master = await getDynamoDB(table.Master, null, corporationNumber, {
      CorporationNumber: corporationNumber,
      MasterId: viewId.securitiesReportInfo
    })

    if (!master[0]) throw master

    return master[0]
  } catch (error) {
    console.error(error)
    errorLog('E000001', userId, viewId.securitiesReportInfo, 'fetchMaster', error)
  }
}

/**
 * @param {any} store
 * @funtion fetchYearlyDataTask
 *  タスクを取得
 */
export async function fetchYearlyDataTask(store) {
  const { corporationNumber, userId, accPeriod: _accPeriod } = _getSessionData(store)
  try {
    const dataCreateTask = await getDataCreateTask(corporationNumber, corporationNumber)

    if (!dataCreateTask) throw ClientError()

    const yearlyTasks = dataCreateTask.filter(
      (e) => e.PeriodKind === periodKind.year.value && Boolean(e.CreateDateTime)
    )

    const createTasks = yearlyTasks.map((e) => {
      const periodValue = e.PeriodValue // yyyy年mm月期
      const fiscalYearStr = convertFiscalPeriodToYear(periodValue, _accPeriod)
      const lastDay = getLastDateOfFiscalPeriod(periodValue, _accPeriod)
      const yearValue = extractYearFromFiscalYear(fiscalYearStr)

      return {
        fiscalYear: fiscalYearStr,
        fiscalYearMonth: lastDay,
        yearValue: yearValue,
        periodKind: e.PeriodKind,
        periodValue: e.PeriodValue,
        dataFixStatus: e.DataFixStatus
      }
    })

    return await createExtraYearList(createTasks, _accPeriod)
  } catch (error) {
    console.error(error)
    errorLog('E000001', userId, viewId.securitiesReportInfo, 'fetchYearlyDataTask', error)
  }
}

/** =------------------------------------------------------------- */

/**
 * @function fetchSecuritiesConfig
 * @param {any} store
 * @param {string} fiscal_period
 * @returns {Promise<ReturnType<import('./types').assertSecuritiesReportResponseSchema>>}
 */
export async function fetchSecuritiesConfig(store, fiscal_period) {
  const { corporationNumber, userId, accPeriod } = _getSessionData(store)

  try {
    const init = {
      headers: {},
      queryStringParameters: {
        fiscal_period: convertFiscalYearToPeriod(fiscal_period, accPeriod)
      }
    }

    const securitiesConfig = await httpGet(
      restApiKey.CHROFY_REST_API,
      PathInfoList.Resource.getSecuritiesReport(corporationNumber),
      init
    )

    if (!securitiesConfig) throw new Error('データがありません')

    return securitiesConfig
  } catch (error) {
    console.error(error)
    errorLog('E000001', userId, viewId.securitiesReportInfo, 'fetchSecuritiesConfig', error)
  }
}

/**
 * @function convertStateToRequest リクエスト用状態の型を変換
 * @param {any} session
 */
const convertStateToRequest = (session) => {
  const { corporationNumber, userName, state, accPeriod } = session

  /** @type {Record<string, import('./types').PageStateValueSchema>} */
  const pageState = PageState.parse(state.pageState)

  const currentPageState = pageState[state.selectedFiscalYear]

  const { companyConfig, groupCompany } = currentPageState
  const { tempEmpCalcBase, tempEmpCalcMethod, genderStatsAll, genderStatsRegular, genderStatsNonRegular } =
    companyConfig

  return assertSecuritiesReportPostRequest({
    corporation_number: corporationNumber,
    user_name: userName,
    fiscal_period: convertFiscalYearToPeriod(state.selectedFiscalYear, accPeriod),
    company_config: {
      key: 'SecuritiesReport',
      comp_number: corporationNumber,
      temp_emp_calc_base: tempEmpCalcBase,
      temp_emp_calc_method: tempEmpCalcMethod,
      gender_stats_all: genderStatsAll,
      gender_stats_regular: genderStatsRegular,
      gender_stats_non_regular: genderStatsNonRegular
    },
    group_company: groupCompany.map((e) => {
      const { searchKey, subCorporationNumber, employeeCountInfo, genderStatisticsInfo, isChrofyEnabled } = e
      const { femaleManagerRatio, parentalLeaveRate, wageGap } = genderStatisticsInfo
      const includeInSubmittingCompanyStatus = isChrofyEnabled
        ? employeeCountInfo.includeInSubmittingCompanyStatus
        : 'none'

      return {
        search_key: searchKey,
        comp_number: subCorporationNumber,
        aggregation_target: employeeCountInfo.aggregationTarget,
        include_in_submitting_company_status: includeInSubmittingCompanyStatus,
        female_manager_ratio: isChrofyEnabled ? femaleManagerRatio : 'none',
        parental_leave_rate: isChrofyEnabled ? parentalLeaveRate : 'none',
        wage_gap: isChrofyEnabled ? wageGap : 'none'
      }
    })
  })
}

/**
 * @function updateSecuritiesReportConfig
 * @param {any} store
 */
export async function updateSecuritiesConfig(store) {
  const session = _getSessionData(store)
  const { corporationNumber, userId } = session

  try {
    const init = {
      headers: {},
      body: convertStateToRequest(session)
    }

    await httpPost(restApiKey.CHROFY_REST_API, PathInfoList.Resource.updateSecuritiesReport(corporationNumber), init)
  } catch (error) {
    errorLog('E000001', userId, viewId.securitiesReportInfo, 'updateSecuritiesConfgi', error)
    throw error
  }
}
